import React from 'react';
import { I18n } from '@aws-amplify/core';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import type { BeraReportV2 } from '@/types/Bera';
import { hasPatchedSegment } from '@/components/views/BeraJob/BeraReportV2/utils';

import { buildStretchesLabel } from './utils';
import { CardInfo, CardStretchCount, CardTitle, ReviewStretchButton, RpnTag, StatusTag, StretchCard } from './styles';

const EVALUATED_COLOR = 'green';
const NOT_EVALUATED_COLOR = 'default';

interface CardProps {
	report: BeraReportV2;
	isSelected: boolean;
	outOfRange?: boolean;
	disabled?: boolean;
	hideTimes?: boolean;
	onSelect: (report: BeraReportV2) => void;
	onHover: (taskId: string | null) => void;
}

interface StatusProps {
	report: BeraReportV2;
	reviewNeeded: boolean;
	disabled: boolean;
}

function Status({ report, reviewNeeded, disabled }: Readonly<StatusProps>): JSX.Element {
	if (reviewNeeded) {
		return (
			<ReviewStretchButton type="link" disabled={disabled}>
				{I18n.get('Review stretch')}
			</ReviewStretchButton>
		);
	}

	const hasRpn = report.rpn !== null && report.rpn !== undefined;

	if (report.is_evaluated && hasRpn) {
		return <RpnTag>{`${I18n.get('RPN')}: ${report.rpn}`}</RpnTag>;
	}

	if (report.is_evaluated) {
		return (
			<StatusTag color={EVALUATED_COLOR} icon={<CheckCircleOutlined />}>
				{I18n.get('Evaluated')}
			</StatusTag>
		);
	}

	return (
		<StatusTag color={NOT_EVALUATED_COLOR} icon={<ExclamationCircleOutlined />}>
			{I18n.get('Not evaluated')}
		</StatusTag>
	);
}

export function Card({
	report,
	isSelected,
	outOfRange = false,
	disabled = false,
	hideTimes = false,
	onSelect,
	onHover
}: Readonly<CardProps>): JSX.Element {
	const stretchesLabel = buildStretchesLabel(report.segments ?? []);

	const isReviewNeeded = outOfRange || hasPatchedSegment(report);

	function handleSelect(): void {
		if (disabled) {
			return;
		}

		onSelect(report);
	}

	function handleMouseEnter(): void {
		onHover(report.task_id);
	}

	function handleMouseLeave(): void {
		onHover(null);
	}

	return (
		<StretchCard
			$isSelected={isSelected}
			$isDisabled={disabled}
			$isOutOfRange={outOfRange}
			aria-disabled={disabled}
			onClick={handleSelect}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<CardInfo>
				<CardTitle>{report.task_name}</CardTitle>
				{!hideTimes && <CardStretchCount>{stretchesLabel}</CardStretchCount>}
			</CardInfo>
			<Status report={report} reviewNeeded={isReviewNeeded} disabled={disabled} />
		</StretchCard>
	);
}
